import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Collapse } from 'react-bootstrap';
import { getItem, getLocalizedPathFromLocalStorage } from '../../helper/localstorage.helper';
import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { PillTabs } from '../../components/dashboard/pill-tabs.component';
import '../../scss/faq-dashboard.scss';
import { faqData, faqData3, ABOUT_INVOZIO_REDIRECT, getAboutInvozioRedirect } from '../../constant/faq-question.constant'

type UnifiedFAQ = {
    id?: string;
    question?: string;
    answer?: string;
    title?: string;
    description?: string;
    category?: 'general' | 'login' | 'features' | 'pension';
    faqCategory?: FaqCategory;
    para1?: string;
    para2?: string;
    para3?: string;
    para4?: string;
    para5?: string;
    para6?: string;
    para7?: string;
    para8?: string;
    para9?: string;
    para10?: string;
    li1?: string[];
    li2?: string[];
    heading?: string;
    heading1?: string;
    heading2?: string;
    head?: string;
    subDescription?: string;
    supportEmail?: string;
    supportEmail1?: string;
    supportEmail2?: string;
    support?: string;
    support1?: string;
    support2?: string;
    link?: string;
    redirect?: string;
    span?: string;
    optionalLinks?: Array<{
        link: string;
        redirect: string;
    }>;
};

type CategoryKey = 'all' | 'invoicing' | 'payments' | 'taxes' | 'tax-free-mileage' | 'travel-allowance' | 'account';
type FaqCategory = Exclude<CategoryKey, 'all'>;

const CATEGORY_FILTERS: { key: CategoryKey; labelKey: string }[] = [
    { key: 'all', labelKey: 'FAQ_CAT_ALL' },
    { key: 'invoicing', labelKey: 'FAQ_CAT_INVOICING' },
    { key: 'payments', labelKey: 'FAQ_CAT_PAYMENTS' },
    { key: 'taxes', labelKey: 'FAQ_CAT_TAXES' },
    { key: 'tax-free-mileage', labelKey: 'FAQ_TAX_FREE_MILEAGE_CATEGORY' },
    { key: 'travel-allowance', labelKey: 'FAQ_CAT_TRAVEL_ALLOWANCE' },
    { key: 'account', labelKey: 'FAQ_CAT_ACCOUNT' },
];

const CATEGORY_LABEL: Record<FaqCategory, string> = {
    invoicing: 'FAQ_CAT_INVOICING',
    payments: 'FAQ_CAT_PAYMENTS',
    taxes: 'FAQ_CAT_TAXES',
    'tax-free-mileage': 'FAQ_TAX_FREE_MILEAGE_CATEGORY',
    'travel-allowance': 'FAQ_CAT_TRAVEL_ALLOWANCE',
    account: 'FAQ_CAT_ACCOUNT',
};

// Derive a display category from the question/answer text (EN + SV keywords),
// since the underlying data is mostly tagged "general". Order = priority.
const deriveCategory = (text: string): FaqCategory => {
    const t = text.toLowerCase();
    if (/(tax|vat|moms|skatt|deduction|avdrag)/.test(t)) return 'taxes';
    if (/(paid|salary|payout|payment|betal|lön|bank|paypal|revolut)/.test(t)) return 'payments';
    if (/(invoice|faktura|logo|logga|send|skicka)/.test(t)) return 'invoicing';
    return 'account';
};

const HelpCircleIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
    </svg>
);

export const FaqDashboardPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [openItems, setOpenItems] = useState<string[]>([]);
    const [activeCategory, setActiveCategory] = useState<CategoryKey>('all');
    const [isLoading, setIsLoading] = useState(true);
    const isLoggedIn = JSON.parse(getItem('user') || 'null');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Skeleton loading state before revealing real content
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 900);
        return () => clearTimeout(timer);
    }, []);

    // Transform faqData3 to match the unified structure
    const transformedFaqData3 = faqData3.map((item, index) => ({
        id: `ext-${index + 1}`,
        question: item.title,
        answer: item.description,
        category: 'general' as const,
        ...item
    }));

    const allFAQs = useMemo(
        () => [...faqData, ...transformedFaqData3] as UnifiedFAQ[],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Tag each FAQ with a derived display category (memoised, translation-aware)
    const taggedFAQs = useMemo(
        () => allFAQs.map((faq, index) => ({
            faq,
            faqId: faq.id || `faq-${index}`,
            displayCategory: faq.faqCategory || deriveCategory(`${t(faq.question || '')} ${t(faq.answer || '')}`),
        })),
        [allFAQs, t]
    );

    // Build a single regex for the WHOLE search phrase (not split per word),
    // so only the exact typed phrase matches/highlights. Case-insensitive.
    const search = useMemo(() => {
        const query = searchQuery.trim();
        if (!query) return null;
        return { pattern: query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') };
    }, [searchQuery]);

    // Does the phrase occur (contiguously) inside this text?
    const textMatches = (text: string) =>
        !!search && new RegExp(search.pattern, 'i').test(text);

    // Filter by search phrase + active category
    const filteredFAQs = taggedFAQs.filter(({ faq, displayCategory }) => {
        if (activeCategory !== 'all' && displayCategory !== activeCategory) {
            return false;
        }

        if (!search) return true;

        const fields = [
            faq.question, faq.answer, faq.subDescription,
            faq.para1, faq.para2, faq.para3, faq.para4, faq.para5,
            faq.para6, faq.para7, faq.para8, faq.para9,
            faq.heading1, faq.heading2, faq.heading, faq.head,
        ];
        for (const field of fields) {
            if (field && typeof field === 'string' && textMatches(t(field))) {
                return true;
            }
        }

        for (const list of [faq.li1, faq.li2]) {
            if (list && list.some(item => textMatches(t(item)))) {
                return true;
            }
        }

        return false;
    });

    // Some list strings start with a literal "•" bullet — strip it so it doesn't
    // double up with the styled CSS bullet on <li> items. Display-only.
    const stripBullet = (text: string) => text.replace(/^\s*•[\s\t]*/, '');

    // Highlight the FULL searched phrase inside a piece of text (one contiguous
    // match), honouring the same Match Case / Whole Word options.
    const highlight = (text: string): React.ReactNode => {
        if (!search || !text) return text;

        const parts = text.split(new RegExp(`(${search.pattern})`, 'gi'));
        // split with a single capture group => odd indices are the matches
        return parts.map((part, i) =>
            i % 2 === 1
                ? <mark key={i} className="faq-highlight">{part}</mark>
                : part
        );
    };

    const toggleItem = (id: string) => {
        setOpenItems(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [id]
        );
    };

    const handleCategoryChange = (key: CategoryKey) => {
        setActiveCategory(key);
        setOpenItems([]); // reset open accordion when switching category
    };

    const goToHowItWorks = () => {
        // Navigate straight to the correctly-localised slug (en: how-works-invozio,
        // sv: hur-fungerar-invozio) so the route-localization guard doesn't bounce
        // the wrong-language path — that redirect was eating the first click(s).
        navigate(
            getLocalizedPathFromLocalStorage(
                { get: (key: string) => getItem(key) ?? null },
                { 'sv-se': 'hur-fungerar-invozio', 'en-us': 'how-works-invozio' }
            )
        );
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar activeItemId='faq-help'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />

                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />

                    <div className="px-3 faq-help-premium">
                        {/* Hero */}
                        <section className="faq-hero fade-up">
                            <span className="faq-hero-badge">
                                <HelpCircleIcon />
                                {t('FAQ_HELP_BADGE')}
                            </span>
                            <h1 className="faq-hero-title">
                                {t('FAQ_HELP_TITLE_LEAD')}{' '}
                                <span className="faq-hero-title-accent">{t('FAQ_HELP_TITLE_ACCENT')}</span>
                            </h1>
                            <p className="faq-hero-subtitle">{t('FAQ_HELP_SUBTITLE')}</p>

                            <button
                                type="button"
                                className="faq-how-it-works-btn"
                                onClick={goToHowItWorks}
                            >
                                <span className="faq-hiw-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                {t('HOW_IT_WORKS')}
                                <svg className="faq-hiw-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>

                            <div className="faq-search-input">
                                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" className="search-icon" aria-hidden="true">
                                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder={t('SEARCH')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="search-field"
                                    aria-label={t('SEARCH')}
                                />
                            </div>

                            <div className="faq-categories-wrap fade-up">
                                <PillTabs
                                    ariaLabel={t('FAQ_CAT_ALL')}
                                    activeTab={activeCategory}
                                    onTabChange={(key) => handleCategoryChange(key as CategoryKey)}
                                    tabs={CATEGORY_FILTERS.map(cat => ({ key: cat.key, label: t(cat.labelKey) }))}
                                />
                            </div>
                        </section>

                        {/* FAQ list */}
                        <div className="faq-dashboard-content">
                            {isLoading ? (
                                <div className="faq-items-list" aria-hidden="true">
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <div key={i} className="faq-skeleton-card">
                                            <span className="sk-bar sk-badge" />
                                            <span className="sk-bar sk-line" />
                                            <span className="sk-circle" />
                                        </div>
                                    ))}
                                </div>
                            ) : filteredFAQs.length === 0 ? (
                                <div className="faq-empty-state fade-up">
                                    <div className="faq-empty-orb">
                                        <span className="faq-empty-ring" />
                                        <span className="faq-empty-ring faq-empty-ring--2" />
                                        <div className="faq-empty-icon">
                                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                    <h3 className="faq-empty-title">{t('NO_SEARCH_RESULTS')}</h3>
                                </div>
                            ) : (
                                <ul className="faq-items-list">
                                    {filteredFAQs.map(({ faq, faqId, displayCategory }, index) => {
                                        const faqQuestion = faq.question || '';
                                        const faqAnswer = faq.answer || '';
                                        const isOpen = openItems.includes(faqId);

                                        return (
                                            <li
                                                key={faqId}
                                                className={`faq-item-dashboard fade-up ${isOpen ? 'open' : ''}`}
                                                style={{ animationDelay: `${index * 50}ms` }}
                                            >
                                                <button
                                                    type="button"
                                                    className="faq-item-header-dashboard"
                                                    onClick={() => toggleItem(faqId)}
                                                    aria-expanded={isOpen}
                                                >
                                                    <span className="faq-question-wrap">
                                                        <span className="faq-category-badge">{t(CATEGORY_LABEL[displayCategory])}</span>
                                                        <span className="faq-question">{highlight(t(faqQuestion))}</span>
                                                    </span>
                                                    <span className="faq-chevron-icon" aria-hidden="true">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </button>

                                                <Collapse in={isOpen}>
                                                    <div className="faq-item-answer">
                                                        <div className="faq-answer-content">
                                                            <p style={{ whiteSpace: 'pre-line' }}>
                                                                {highlight(t(faqAnswer))}
                                                            </p>
                                                            {faq.subDescription && <p>{highlight(t(faq.subDescription))}</p>}
                                                            {faq.heading1 && <h6 className="fw-bold">{highlight(t(faq.heading1))}</h6>}
                                                            {faq.para1 && typeof faq.para1 === 'string' && <p>{highlight(t(faq.para1))}</p>}
                                                            {faq.supportEmail && (
                                                                <a href={faq.support} className="text-decoration-underline d-inline-block mb-2">
                                                                    {faq.supportEmail}
                                                                </a>
                                                            )}
                                                            {faq.para2 && typeof faq.para2 === 'string' && <p>{highlight(t(faq.para2))}</p>}
                                                            {faq.para3 && typeof faq.para3 === 'string' && <p>{highlight(t(faq.para3))}</p>}
                                                            {faq.li1 && (
                                                                <ul>
                                                                    {faq.li1.map((item, liIndex) => (
                                                                        <li key={liIndex}>{highlight(stripBullet(t(item)))}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                            {faq.heading2 && <h6 className="fw-bold">{highlight(t(faq.heading2))}</h6>}
                                                            {faq.para4 && typeof faq.para4 === 'string' && <p>{highlight(t(faq.para4))}</p>}
                                                            {faq.para5 && typeof faq.para5 === 'string' && <p>{highlight(t(faq.para5))}</p>}
                                                            {faq.para6 && typeof faq.para6 === 'string' && <p>{highlight(t(faq.para6))}</p>}
                                                            {faq.para7 && typeof faq.para7 === 'string' && <p>{highlight(t(faq.para7))}</p>}
                                                            {faq.para8 && typeof faq.para8 === 'string' && <p>{highlight(t(faq.para8))}</p>}
                                                            {faq.supportEmail2 && (
                                                                <a href={faq.support2} className="text-decoration-underline d-inline-block mb-2">
                                                                    {faq.supportEmail2}
                                                                </a>
                                                            )}
                                                            {faq.para9 && typeof faq.para9 === 'string' && <p>{highlight(t(faq.para9))}</p>}
                                                            {faq.link && !(faq.id === '58' && !isLoggedIn) && (
                                                                <a href={faq.redirect === ABOUT_INVOZIO_REDIRECT ? getAboutInvozioRedirect(i18n.language) : faq.redirect} target="_blank" rel="noopener noreferrer" className="text-decoration-underline d-inline-block mb-2">
                                                                    {t(faq.link)}
                                                                </a>
                                                            )}
                                                            {faq.head && <h6 className="fw-bold">{highlight(t(faq.head))}</h6>}
                                                            {faq.optionalLinks && (
                                                                <div className="mb-2">
                                                                    {faq.optionalLinks.map((linkItem, linkIndex) => (
                                                                        <p key={linkIndex} className="mb-1">
                                                                            <a href={linkItem.redirect} target="_blank" rel="noopener noreferrer" className="text-decoration-underline d-inline-block">
                                                                                {highlight(t(linkItem.link))}
                                                                            </a>
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {faq.li2 && (
                                                                <ul>
                                                                    {faq.li2.map((item, liIndex) => (
                                                                        <li key={liIndex}>{highlight(stripBullet(t(item)))}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                            {faq.supportEmail1 && (
                                                                <a href={faq.support1} className="text-decoration-underline d-inline-block">
                                                                    {faq.supportEmail1}
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FaqDashboardPage;
